import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { makeStyles } from 'tss-react/mui';
import mergeSort from '../../../../../../Utils/mergeSort';
import {
  TEDGAGravKontrollSTATUSEnum,
  TEDGAGravanordningStenStatusEnum,
} from '../../../../../../globalQueryTypes';
import {
  mapFormToKontrollValues,
  STATUS_LABELS,
  STEN_STATUS_LABELS,
} from '../Utils/anordningarUtils';
import GravplatsUpdateMutation from '../Mutations/GravplatsUpdateMutation.gql';
import clientAvenyModel from '../../../../../../Model/clientAvenyModel';
import { GravplatsQuery_node_TEDGAGrav as IGravplatsNode } from '../Queries/_QueryTypes/GravplatsQuery';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import GravTitelKort from '../GravTitelKort';
import { ObjektVyKort } from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import ObjektVySida from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKolumner/ObjektVyKolumner';
import { useBusinessDataFormContext } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import EDCaptionedValue from '../../../../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import EDList from '../../../../../../BaseComponents/List/EDList';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import EDDialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import EDRadioGroup from '../../../../../../BaseComponents/RadioGroup/EDRadioGroup';
import EDGrid from '../../../../../../BaseComponents/GridComponents/EDGrid';
import EDCheckbox from '../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDNumericEdit from '../../../../../../BaseComponents/NumericEdit/EDNumericEdit';
import { EDTextEdit } from '../../../../../../BaseComponents/TextEdit/EDTextEdit';
import EDListItem, {
  EDListItemButton,
} from '../../../../../../BaseComponents/List/Components/EDListItem';
import EDAvenyProgress from '../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import BildViewer from '../BildViewer';
import BildVäljare from '../BildVäljare';
import { uploadFile } from '../../../../../../Utils/fileUtils';

const GravModel = clientAvenyModel.GA.BusinessModels.GAGrav;

interface IAnordningarProps {
  sidNr: number;
  nodeData: IGravplatsNode | null | undefined;
  fullPage: boolean;
}

const useStyles = makeStyles()(theme => ({
  item: {
    '&:hover': {
      background:
        theme.customProperties?.Fade?.BlueGray?.hover ??
        theme.palette.action.hover,
    },
  },
  selectedItem: {
    background:
      theme.customProperties?.Fade?.BlueGray?.selected ??
      theme.palette.action.selected,
  },
  label: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(0.25),
    },
  },
  valuesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.5, 1.5),
    },
  },
  valueItem: {
    minWidth: 120,
    [theme.breakpoints.down('sm')]: {
      minWidth: 80,
    },
  },
  valueLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.7rem',
    },
  },
  valueText: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  secondaryActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  listItemContent: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    padding: theme.spacing(1, 0),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5, 0),
    },
  },
  paper: {
    maxWidth: '670px',
    background: theme.palette.background.default,
  },
}));

export const Anordningar: React.FC<IAnordningarProps> = ({
  sidNr,
  nodeData,
  fullPage,
}) => {
  const formContext = useBusinessDataFormContext<IGravplatsNode>();
  const { activeDatabase } = React.useContext(ActiveDatabaseContext);
  const { classes, cx } = useStyles();
  const [selectedAnordning, setSelectedAnordning] = useState<any>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyAnordning, setHistoryAnordning] = useState<any>(null);
  const [newInventeringDialogOpen, setNewInventeringDialogOpen] =
    useState(false);
  const [newInventeringAnordning, setNewInventeringAnordning] =
    useState<any>(null);

  // States för "ny inventering"
  const [inventeringStatus, setInventeringStatus] = useState('');
  const [atgard, setAtgard] = useState('');
  const [forKortaDubb, setForKortaDubb] = useState(false);
  const [forKraftigLutning, setForKraftigLutning] = useState(false);
  const [forStoraHal, setForStoraHal] = useState(false);
  const [losaDelar, setLosaDelar] = useState(false);
  const [sockelSaknas, setSockelSaknas] = useState(false);
  const [ovrigaAnmarkningar, setOvrigaAnmarkningar] = useState(false);
  const [lutning, setLutning] = useState<number | undefined>(undefined);
  const [anteckningar, setAnteckningar] = useState('');
  const [kontrollBildBlobId, setKontrollBildBlobId] = useState<
    string | undefined
  >(undefined);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveGravKontrollMutation, { loading: saveLoading }] = useMutation(
    GravplatsUpdateMutation
  );

  const anordningar = useMemo(
    () => nodeData?._Associations?.Anordningar_All ?? [],
    [nodeData]
  );

  //  fallback till första itemet i anordingar om inget annat valts
  const effectiveSelectedAnordning =
    selectedAnordning ?? anordningar[0] ?? null;

  const getValues = (item: any) =>
    [
      { label: 'Material', value: item?.anmaterial },
      { label: 'Höjd', value: item?.ANHOJD ? `${item.ANHOJD} m` : null },
      { label: 'Beskrivning', value: item?.ANBESKRIVNING },
      { label: 'Datum', value: item?.StenDatum },
      {
        label: 'Status',
        value:
          STEN_STATUS_LABELS[
            item?.StenStatus as TEDGAGravanordningStenStatusEnum
          ] ??
          item?.StenStatus ??
          null,
      },
      { label: 'Signatur', value: item?.StenSign },
    ].filter(v => v.value);

  const handleSelectAnordning = (item: any) => {
    setSelectedAnordning(item);
  };

  const handleOpenHistoryDialog = (item: any) => {
    setHistoryAnordning(item);
    setHistoryDialogOpen(true);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setHistoryAnordning(null);
  };

  const handleOpenNewInventeringDialog = (item: any) => {
    setNewInventeringAnordning(item);
    setInventeringStatus('');
    setAtgard('');
    setForKortaDubb(false);
    setForKraftigLutning(false);
    setForStoraHal(false);
    setLosaDelar(false);
    setSockelSaknas(false);
    setOvrigaAnmarkningar(false);
    setLutning(undefined);
    setAnteckningar('');
    setKontrollBildBlobId(undefined);
    setUploadLoading(false);
    setUploadError(null);
    setSaveError(null);
    setNewInventeringDialogOpen(true);
  };

  const handleCloseNewInventeringDialog = () => {
    setNewInventeringDialogOpen(false);
    setNewInventeringAnordning(null);
    setKontrollBildBlobId(undefined);
    setUploadError(null);
    setSaveError(null);
  };

  const handleSaveKontrollBild = async (file: File) => {
    if (!activeDatabase) {
      setUploadError('Kunde inte ladda upp bild. Saknar aktiv databas.');
      return;
    }

    setUploadError(null);
    setUploadLoading(true);
    try {
      const blobId = await uploadFile(file, activeDatabase);
      setKontrollBildBlobId(blobId);
    } catch {
      setUploadError('Något gick fel när bilden skulle laddas upp.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleRemoveKontrollBild = () => {
    setKontrollBildBlobId(undefined);
    setUploadError(null);
  };

  const isNyInventeringFormValid = () => {
    if (!inventeringStatus) {
      return false;
    }

    if (inventeringStatus === 'ej_godkand' && !atgard) {
      return false;
    }

    return true;
  };

  const handleSaveNewInventering = async () => {
    if (!isNyInventeringFormValid()) {
      return;
    }

    const anordningId = newInventeringAnordning?.id;
    const anordningVersion = newInventeringAnordning?._version;
    const gravId = nodeData?.id;
    const gravVersion = nodeData?._version;
    if (
      !anordningId ||
      anordningVersion == null ||
      !gravId ||
      gravVersion == null ||
      !activeDatabase
    ) {
      setSaveError('Kunde inte spara inventeringen.');
      return;
    }

    const currentYear = new Date().getFullYear().toString();

    try {
      setSaveError(null);
      await saveGravKontrollMutation({
        variables: {
          db: activeDatabase,
          skotselAr: currentYear,
          values: {
            id: gravId,
            _version: gravVersion,
            _Associations: {
              Anordningar_Update_Values: [
                {
                  id: anordningId,
                  _version: anordningVersion,
                  _Associations: {
                    Stenkontroll_Add_Values: [
                      mapFormToKontrollValues(
                        {
                          inventeringStatus: inventeringStatus as
                            | 'godkand'
                            | 'ej_godkand',
                          atgard,
                          forKortaDubb,
                          forKraftigLutning,
                          forStoraHal,
                          losaDelar,
                          sockelSaknas,
                          ovrigaAnmarkningar,
                          lutning: lutning != null ? String(lutning) : '',
                          anteckningar,
                        },
                        gravId,
                        kontrollBildBlobId
                      ),
                    ],
                  },
                },
              ],
            },
          },
        },
      });

      await formContext?.refetchData?.();
      handleCloseNewInventeringDialog();
    } catch {
      setSaveError('Något gick fel när inventeringen skulle sparas.');
    }
  };

  const handleLutningChange = (newValue: number | null) => {
    if (newValue == null) {
      setLutning(undefined);
      return;
    }

    setLutning(Math.trunc(newValue));
  };

  const disableSaveNewInventering =
    !isNyInventeringFormValid() || saveLoading || uploadLoading;
  const inventeringLoading = saveLoading || uploadLoading;

  return (
    <ObjektVySida
      sidNr={sidNr}
      caption={'Anordningar'}
      collectionName={GravModel.CollectionName}
    >
      <ObjektVyKolumner>
        <ObjektVyKolumn fullPage={fullPage}>
          <GravTitelKort nodeData={nodeData} />

          <ObjektVyKort
            className="position-relative"
            backgroundColor={'white'}
            caption="Anordningar"
          >
            {anordningar.length > 0 ? (
              <EDList noPadding>
                {anordningar.map((item, index) => {
                  const values = getValues(item);
                  const isSelected = selectedAnordning?.id === item?.id;
                  return (
                    <EDListItemButton
                      key={item?.id ?? index}
                      className={cx(classes.item, {
                        [classes.selectedItem]: isSelected,
                      })}
                      divider={index < anordningar.length - 1}
                      disableVerticalPadding={false}
                      selected={isSelected}
                      onClick={() => handleSelectAnordning(item)}
                    >
                      <EDBox className={classes.listItemContent}>
                        <EDBox sx={{ flex: 1 }}>
                          <EDTypography
                            variant="subtitle2"
                            className={classes.label}
                          >
                            {item?.ANBENAMNING || '-'}
                          </EDTypography>

                          <div className={classes.valuesContainer}>
                            {values.map((v, idx) => (
                              <div key={idx} className={classes.valueItem}>
                                <EDTypography
                                  variant="caption"
                                  className={classes.valueLabel}
                                >
                                  {v.label}
                                </EDTypography>
                                <EDTypography
                                  variant="body2"
                                  className={classes.valueText}
                                >
                                  {v.value || '-'}
                                </EDTypography>
                              </div>
                            ))}
                          </div>
                        </EDBox>

                        <div className={classes.secondaryActions}>
                          <EDIconButton
                            icon="add"
                            iconSize="medium"
                            margin="none"
                            padding="dense"
                            onClick={e => {
                              e.stopPropagation();
                              handleOpenNewInventeringDialog(item);
                            }}
                            tooltip="Ny inventering"
                          />
                          <EDIconButton
                            icon="eye_outline"
                            iconSize="medium"
                            margin="none"
                            padding="dense"
                            onClick={e => {
                              e.stopPropagation();
                              handleOpenHistoryDialog(item);
                            }}
                            tooltip="Tidigare inventeringar"
                          />
                        </div>
                      </EDBox>
                    </EDListItemButton>
                  );
                })}
              </EDList>
            ) : (
              <EDTypography>Inga anordningar registrerade</EDTypography>
            )}
          </ObjektVyKort>
        </ObjektVyKolumn>

        <ObjektVyKolumn fullPage={fullPage}>
          {anordningar.length > 0 && effectiveSelectedAnordning ? (
            <ObjektVyKort
              className="position-relative"
              backgroundColor={'white'}
              caption="Detaljer"
            >
              {effectiveSelectedAnordning?._Associations?.Bild?.BILD ? (
                <div>
                  <BildViewer
                    blobId={effectiveSelectedAnordning._Associations.Bild.BILD}
                    alt="Anordningsfoto"
                    style={{ height: '200px', objectFit: 'cover' }}
                    dialogTitle="Anordningsfoto"
                  />
                </div>
              ) : (
                <EDTypography>Foto saknas</EDTypography>
              )}

              {effectiveSelectedAnordning?._Associations?.Bild?.BILD_ANDRAD && (
                <EDCaptionedValue
                  label="Senast uppdaterad"
                  name="bild_andrad"
                  size="large"
                >
                  <EDTypography>
                    {effectiveSelectedAnordning._Associations.Bild.BILD_ANDRAD}
                  </EDTypography>
                </EDCaptionedValue>
              )}

              <EDCaptionedValue label="Benämning" name="benamning" size="large">
                <EDTypography>
                  {effectiveSelectedAnordning?.ANBENAMNING || '-'}
                </EDTypography>
              </EDCaptionedValue>

              <EDCaptionedValue label="Status" name="status" size="large">
                <EDTypography>
                  {STEN_STATUS_LABELS[
                    effectiveSelectedAnordning?.StenStatus as TEDGAGravanordningStenStatusEnum
                  ] ??
                    effectiveSelectedAnordning?.StenStatus ??
                    '-'}
                </EDTypography>
              </EDCaptionedValue>

              <EDCaptionedValue label="Material" name="material" size="large">
                <EDTypography>
                  {effectiveSelectedAnordning?.anmaterial || '-'}
                </EDTypography>
              </EDCaptionedValue>

              <EDCaptionedValue label="Höjd" name="hojd" size="large">
                <EDTypography>
                  {effectiveSelectedAnordning?.ANHOJD
                    ? `${effectiveSelectedAnordning.ANHOJD} m`
                    : '-'}
                </EDTypography>
              </EDCaptionedValue>
              <EDCaptionedValue label="Bredd" name="bredd" size="large">
                <EDTypography>
                  {effectiveSelectedAnordning?.ANBREDD
                    ? `${effectiveSelectedAnordning.ANBREDD} m`
                    : '-'}
                </EDTypography>
              </EDCaptionedValue>
              <EDCaptionedValue label="Tjocklek" name="tjocklek" size="large">
                <EDTypography>
                  {effectiveSelectedAnordning?.ANTJOCKLEK
                    ? `${effectiveSelectedAnordning.ANTJOCKLEK} m`
                    : '-'}
                </EDTypography>
              </EDCaptionedValue>

              <EDCaptionedValue
                label="Beskrivning"
                name="beskrivning"
                size="large"
              >
                <EDTypography>
                  {effectiveSelectedAnordning?.ANBESKRIVNING || '-'}
                </EDTypography>
              </EDCaptionedValue>

              <EDCaptionedValue
                label="Beslut om anordning vid återlämnande"
                name="aterlamnande"
                size="large"
              >
                <EDTypography>
                  {effectiveSelectedAnordning?.ANBESLUT || '-'}
                </EDTypography>
              </EDCaptionedValue>

              <EDCaptionedValue label="Namn" name="namn" size="large">
                <EDTypography>
                  {effectiveSelectedAnordning?.ANNAMN || '-'}
                </EDTypography>
              </EDCaptionedValue>

              <EDCaptionedValue
                label="Stenhuggeri"
                name="stenhuggeri"
                size="large"
              >
                <EDTypography>
                  {effectiveSelectedAnordning?.StenhuggeriBen || '-'}
                </EDTypography>
              </EDCaptionedValue>

              <EDCaptionedValue
                label="Anteckningar"
                name="Anteckningar"
                size="large"
              >
                <EDTypography>
                  {effectiveSelectedAnordning?.Anteckningar || '-'}
                </EDTypography>
              </EDCaptionedValue>
            </ObjektVyKort>
          ) : (
            <ObjektVyKort
              className="position-relative"
              backgroundColor={'white'}
              caption="Detaljer"
            >
              <EDTypography>Inga anordningar registrerade</EDTypography>
            </ObjektVyKort>
          )}
        </ObjektVyKolumn>
      </ObjektVyKolumner>

      {/* Dialog för inventeringshistorik */}
      <EDDialog
        open={historyDialogOpen}
        onClose={handleCloseHistoryDialog}
        title="Tidigare inventeringar"
        subtitle={historyAnordning?.ANBENAMNING}
        maxWidth="md"
        fullWidth
        classes={{
          paper: classes.paper,
        }}
        footerActions={[
          {
            label: 'Stäng',
            onClick: handleCloseHistoryDialog,
          },
        ]}
      >
        <EDBox display="flex" flexDirection="column" gap={2}>
          {/* Anordningsinfo */}
          <ObjektVyKort caption="Anordningsdetaljer">
            <EDBox>
              <EDTypography variant="body2" color="textSecondary">
                Material: {historyAnordning?.anmaterial || '-'}
              </EDTypography>
              <EDTypography variant="body2" color="textSecondary">
                Höjd:{' '}
                {historyAnordning?.ANHOJD
                  ? `${historyAnordning.ANHOJD} m`
                  : '-'}
              </EDTypography>
              <EDTypography variant="body2" color="textSecondary">
                Status:{' '}
                {STEN_STATUS_LABELS[
                  historyAnordning?.StenStatus as TEDGAGravanordningStenStatusEnum
                ] ??
                  historyAnordning?.StenStatus ??
                  '-'}
              </EDTypography>
              <EDTypography variant="body2" color="textSecondary">
                Datum: {historyAnordning?.StenDatum || '-'}
              </EDTypography>
            </EDBox>
          </ObjektVyKort>

          {/* Inventeringslista */}
          <ObjektVyKort caption="Inventeringar">
            {(() => {
              const kontroller: any[] =
                historyAnordning?._Associations?.Stenkontroll_All ?? [];

              if (kontroller.length === 0) {
                return (
                  <EDTypography>Inga inventeringar registrerade</EDTypography>
                );
              }

              const sorted = mergeSort([...kontroller], (a, b) =>
                (b.DATUM ?? '').localeCompare(a.DATUM ?? '')
              );

              return (
                <EDList noPadding>
                  {sorted.map((kontroll, index) => {
                    const statusLabel =
                      STATUS_LABELS[
                        kontroll.STATUS as TEDGAGravKontrollSTATUSEnum
                      ] ??
                      kontroll.STATUS ??
                      '-';
                    return (
                      <EDListItem
                        key={kontroll.id ?? index}
                        divider={index < sorted.length - 1}
                        disableRipple
                      >
                        <EDBox
                          sx={{
                            width: '100%',
                            py: 1,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                          }}
                        >
                          <EDBox sx={{ flex: 1 }}>
                            <EDTypography variant="subtitle2" bold mb={0.5}>
                              {kontroll.DATUM ?? '-'}
                            </EDTypography>

                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '12px 24px',
                              }}
                            >
                              {[
                                {
                                  label: 'Stenstatus',
                                  value: statusLabel,
                                  alwaysShow: false,
                                },
                                {
                                  label: 'Signatur',
                                  value: kontroll.SIGNATUR,
                                  alwaysShow: false,
                                },
                                {
                                  label: 'Lutning',
                                  value: kontroll.LUTNING,
                                  alwaysShow: true,
                                },
                                {
                                  label: 'Anmärkningar',
                                  value: kontroll.ANMARKNINGAR,
                                  alwaysShow: false,
                                },
                              ]
                                .filter(v => v.alwaysShow || v.value)
                                .map((v, i) => (
                                  <div key={i}>
                                    <EDTypography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        display: 'block',
                                      }}
                                    >
                                      {v.label}
                                    </EDTypography>
                                    <EDTypography variant="body2">
                                      {v.value ?? '-'}
                                    </EDTypography>
                                  </div>
                                ))}
                            </div>

                            {kontroll.NOTERING && (
                              <EDBox mt={0.5}>
                                <EDTypography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    display: 'block',
                                  }}
                                >
                                  Anteckningar
                                </EDTypography>
                                <EDTypography variant="body2">
                                  {kontroll.NOTERING}
                                </EDTypography>
                              </EDBox>
                            )}
                          </EDBox>

                          {kontroll._Associations?.Bild?.BILD && (
                            <BildViewer
                              blobId={kontroll._Associations.Bild.BILD}
                              alt="Inventeringsfoto"
                              style={{
                                width: '120px',
                                height: '120px',
                                objectFit: 'cover',
                                flexShrink: 0,
                                borderRadius: '4px',
                              }}
                              dialogTitle="Inventeringsfoto"
                            />
                          )}
                        </EDBox>
                      </EDListItem>
                    );
                  })}
                </EDList>
              );
            })()}
          </ObjektVyKort>
        </EDBox>
      </EDDialog>

      {/* Dialog för ny inventering */}
      <EDDialog
        open={newInventeringDialogOpen}
        onClose={handleCloseNewInventeringDialog}
        title="Ny inventering"
        subtitle={newInventeringAnordning?.ANBENAMNING}
        maxWidth="md"
        fullWidth
        classes={{
          paper: classes.paper,
        }}
        footerActions={[
          {
            label: 'Avbryt',
            onClick: handleCloseNewInventeringDialog,
          },
          {
            label: saveLoading ? 'Sparar' : 'Spara',
            onClick: handleSaveNewInventering,
            buttonProps: {
              disabled: disableSaveNewInventering,
            },
          },
        ]}
      >
        <EDBox
          sx={{ position: 'relative' }}
          display="flex"
          flexDirection="column"
          gap={2}
        >
          {inventeringLoading && <EDAvenyProgress overlay />}

          <ObjektVyKort caption="Anordningsdetaljer">
            {/* Anordningsinfo */}
            <EDBox mb={2}>
              {saveError && (
                <EDTypography color="error" mb={1}>
                  {saveError}
                </EDTypography>
              )}
              <EDTypography variant="body2" color="textSecondary">
                Material: {newInventeringAnordning?.anmaterial || '-'}
              </EDTypography>
              <EDTypography variant="body2" color="textSecondary">
                Höjd:{' '}
                {newInventeringAnordning?.ANHOJD
                  ? `${newInventeringAnordning.ANHOJD} m`
                  : '-'}
              </EDTypography>
              <EDTypography variant="body2" color="textSecondary">
                Datum: {newInventeringAnordning?.StenDatum || '-'}
              </EDTypography>
            </EDBox>

            {/* Status inventering */}
            <EDBox mb={3}>
              <EDRadioGroup
                label="Status"
                value={inventeringStatus}
                onChange={value => {
                  setInventeringStatus(value);
                  if (value !== 'ej_godkand') {
                    setAtgard('');
                  }
                }}
                radioDefs={[
                  { label: 'Godkänd', value: 'godkand' },
                  { label: 'Ej godkänd', value: 'ej_godkand' },
                ]}
                required
              />
            </EDBox>

            {/* Åtgärder */}
            {inventeringStatus === 'ej_godkand' && (
              <>
                <EDBox mb={2}>
                  <EDRadioGroup
                    label="Åtgärd"
                    value={atgard}
                    onChange={value => setAtgard(value)}
                    radioDefs={[
                      { label: 'Stöttad', value: 'stottad' },
                      { label: 'Nedlagd', value: 'nedlagd' },
                      { label: 'Ej åtgärdad', value: 'ej_atgardad' },
                      { label: 'Övrigt', value: 'ovrigt' },
                    ]}
                    row
                    marginTop="none"
                    required
                  />
                </EDBox>

                {/* Anmärkningar */}

                <EDBox mb={2}>
                  <EDTypography variant="subtitle2" bold mb={1}>
                    Anmärkningar
                  </EDTypography>
                  <EDGrid container spacing={0}>
                    <EDGrid item xs={6}>
                      <EDCheckbox
                        label="För korta dubb"
                        checked={forKortaDubb}
                        onCheck={setForKortaDubb}
                      />
                    </EDGrid>
                    <EDGrid item xs={6}>
                      <EDCheckbox
                        label="För kraftig lutning"
                        checked={forKraftigLutning}
                        onCheck={setForKraftigLutning}
                      />
                    </EDGrid>
                    <EDGrid item xs={6}>
                      <EDCheckbox
                        label="För stora hål"
                        checked={forStoraHal}
                        onCheck={setForStoraHal}
                      />
                    </EDGrid>
                    <EDGrid item xs={6}>
                      <EDCheckbox
                        label="Lösa delar"
                        checked={losaDelar}
                        onCheck={setLosaDelar}
                      />
                    </EDGrid>
                    <EDGrid item xs={6}>
                      <EDCheckbox
                        label="Sockel saknas"
                        checked={sockelSaknas}
                        onCheck={setSockelSaknas}
                      />
                    </EDGrid>
                    <EDGrid item xs={6}>
                      <EDCheckbox
                        label="Övriga anmärkningar"
                        checked={ovrigaAnmarkningar}
                        onCheck={setOvrigaAnmarkningar}
                      />
                    </EDGrid>
                  </EDGrid>
                </EDBox>
              </>
            )}

            {/* Lutning i grader */}
            <EDNumericEdit
              id="lutning"
              label="Lutning i grader"
              type="number"
              value={lutning}
              onChange={handleLutningChange}
            />

            {/* Anteckningar */}
            <EDTextEdit
              label="Anteckningar"
              value={anteckningar}
              onChange={value => setAnteckningar(value)}
              multiline
              minRows={4}
              fullWidth
            />

            {/* Foto */}
            <EDBox mt={2}>
              <EDTypography variant="subtitle2" bold mb={1}>
                Foto
              </EDTypography>
              <BildVäljare
                onSave={handleSaveKontrollBild}
                dialogTitle="Granska inventeringsfoto"
                disabled={uploadLoading || saveLoading}
              />
              {uploadError && (
                <EDTypography variant="caption" color="error" mt={1}>
                  {uploadError}
                </EDTypography>
              )}
              {kontrollBildBlobId && (
                <EDBox mt={1}>
                  <BildViewer
                    blobId={kontrollBildBlobId}
                    alt="Inventeringsfoto"
                    style={{
                      width: '120px',
                      height: '120px',
                      objectFit: 'cover',
                    }}
                    dialogTitle="Inventeringsfoto"
                  />
                  <EDBox mt={1}>
                    <EDIconButton
                      tooltip="Ta bort foto"
                      icon="delete"
                      margin="none"
                      padding="dense"
                      onClick={handleRemoveKontrollBild}
                      disabled={uploadLoading || saveLoading}
                    />
                  </EDBox>
                </EDBox>
              )}
            </EDBox>
          </ObjektVyKort>
        </EDBox>
      </EDDialog>
    </ObjektVySida>
  );
};

export default Anordningar;
